import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { CacheStatus } from '@/pages/AdvancedEditor/hooks/useUploadTtsCache';
import { TtsType } from '@/services/tts/tts-data';
import { DEFAULT_VOICE_CONFIG, GenerationError } from '@/state/presenters';
import { Emotion } from '@/utils/types';
import { ImageType, IVoice, ScriptType, VoiceConfig } from '@d-id/types';

export enum PolotnoStoreActivePageKeys {
    textInput = 'textInput',
    sceneDurationCalculated = 'sceneDurationCalculated',
    sceneTtsType = 'sceneTtsType',
    ttsIsSelectedDefault = 'ttsIsSelectedDefault',
    ttsError = 'ttsError',
    ttsVoiceConfig = 'ttsVoiceConfig',
    ttsSelectedVoice = 'ttsSelectedVoice',
    ttsAudioCacheStatus = 'ttsAudioCacheStatus',
    ttsAudioError = 'ttsAudioError',
    selectedEmotion = 'selectedEmotion',
    selectedSentimentId = 'selectedSentimentId',
    selectedDriver = 'selectedDriver',
    ttsSelectedLanguage = 'ttsSelectedLanguage',
    selectedPresenterType = 'selectedPresenterType',
    selectedPresenter = 'selectedPresenter',
    selectedScript = 'selectedScript',
    uploadedAudio = 'uploadedAudio',
    isRecordUploading = 'isRecordUploading',
    isRecording = 'isRecording',
    isUploadingAudio = 'isUploadingAudio',
    isAddingAvatar = 'isAddingAvatar',
    wordLevelTimestampsUrl = 'wordLevelTimestampsUrl',
    isThereAnySelectedLockedElement = 'isThereAnySelectedLockedElement',
    ttsCreated = 'ttsCreated',
    ttsCacheVersion = 'ttsCacheVersion',
    cachedAudio = 'cachedAudio',
    thumbnail = 'thumbnail',
    shouldIndicateMissingScript = 'shouldIndicateMissingScript',
}

interface PolotnoStoreActivePageState {
    [PolotnoStoreActivePageKeys.textInput]: string;
    [PolotnoStoreActivePageKeys.ttsIsSelectedDefault]: boolean;
    [PolotnoStoreActivePageKeys.ttsError]: GenerationError | null;
    [PolotnoStoreActivePageKeys.ttsVoiceConfig]: VoiceConfig;
    [PolotnoStoreActivePageKeys.ttsSelectedVoice]: IVoice | null;
    [PolotnoStoreActivePageKeys.ttsAudioCacheStatus]: CacheStatus;
    [PolotnoStoreActivePageKeys.ttsAudioError]: string | null;
    [PolotnoStoreActivePageKeys.selectedEmotion]: Emotion | undefined;
    [PolotnoStoreActivePageKeys.selectedSentimentId]: string | undefined;
    [PolotnoStoreActivePageKeys.selectedDriver]: string | undefined;
    [PolotnoStoreActivePageKeys.ttsSelectedLanguage]: string | null;
    [PolotnoStoreActivePageKeys.selectedPresenterType]: ImageType;
    [PolotnoStoreActivePageKeys.selectedPresenter]: string;
    [PolotnoStoreActivePageKeys.selectedScript]: ScriptType;
    [PolotnoStoreActivePageKeys.uploadedAudio]: AudioData | null;
    [PolotnoStoreActivePageKeys.sceneDurationCalculated]: boolean;
    [PolotnoStoreActivePageKeys.sceneTtsType]: TtsType | null;
    [PolotnoStoreActivePageKeys.isRecordUploading]: boolean;
    [PolotnoStoreActivePageKeys.isRecording]: boolean;
    [PolotnoStoreActivePageKeys.isUploadingAudio]: boolean;
    [PolotnoStoreActivePageKeys.isAddingAvatar]: boolean;
    [PolotnoStoreActivePageKeys.wordLevelTimestampsUrl]: string | undefined;
    [PolotnoStoreActivePageKeys.isThereAnySelectedLockedElement]: boolean;
    [PolotnoStoreActivePageKeys.ttsCreated]: boolean;
    [PolotnoStoreActivePageKeys.ttsCacheVersion]: number;
    [PolotnoStoreActivePageKeys.cachedAudio]: string;
    [PolotnoStoreActivePageKeys.thumbnail]: string;
    [PolotnoStoreActivePageKeys.shouldIndicateMissingScript]: boolean;
}

export const polotnoStorePageDefaults: Record<PolotnoStoreActivePageKeys, { defaultValue: any; persistence: boolean }> = {
    [PolotnoStoreActivePageKeys.textInput]: {
        defaultValue: '',
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.sceneDurationCalculated]: {
        defaultValue: false,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.ttsCreated]: {
        defaultValue: false,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.cachedAudio]: {
        defaultValue: '',
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.ttsCacheVersion]: {
        defaultValue: 0,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.ttsIsSelectedDefault]: {
        defaultValue: true,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.ttsError]: {
        defaultValue: null,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.ttsVoiceConfig]: {
        defaultValue: DEFAULT_VOICE_CONFIG,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.ttsSelectedVoice]: {
        defaultValue: null,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.ttsAudioError]: {
        defaultValue: null,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.ttsAudioCacheStatus]: {
        defaultValue: CacheStatus.Empty,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.selectedEmotion]: {
        defaultValue: undefined,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.selectedSentimentId]: {
        defaultValue: undefined,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.selectedDriver]: {
        defaultValue: undefined,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.ttsSelectedLanguage]: {
        defaultValue: 'English',
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.selectedPresenterType]: {
        defaultValue: ImageType.Upload,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.selectedPresenter]: {
        defaultValue: '',
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.selectedScript]: {
        defaultValue: ScriptType.text,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.uploadedAudio]: {
        defaultValue: null,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.sceneTtsType]: {
        defaultValue: null,
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.isRecordUploading]: {
        defaultValue: false,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.isRecording]: {
        defaultValue: false,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.isUploadingAudio]: {
        defaultValue: false,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.isAddingAvatar]: {
        defaultValue: false,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.wordLevelTimestampsUrl]: {
        defaultValue: undefined,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.isThereAnySelectedLockedElement]: {
        defaultValue: false,
        persistence: false,
    },
    [PolotnoStoreActivePageKeys.thumbnail]: {
        defaultValue: '',
        persistence: true,
    },
    [PolotnoStoreActivePageKeys.shouldIndicateMissingScript]: {
        defaultValue: false,
        persistence: false,
    },
};

type PolotnoActivePageStateValue<K extends PolotnoStoreActivePageKeys> = PolotnoStoreActivePageState[K];

export const usePolotnoActivePageState = <K extends PolotnoStoreActivePageKeys>(
    key: K
): readonly [PolotnoActivePageStateValue<K>, (value: PolotnoActivePageStateValue<K>) => void] => {
    const { store } = useAdvancedEditorContext();
    const { persistence, defaultValue } = polotnoStorePageDefaults[key];

    const storage = persistence ? 'custom' : ('temporary' as unknown as 'custom');

    const setter = (value: PolotnoActivePageStateValue<K>) => {
        store.activePage?.set({
            [storage]: {
                ...(store.activePage?.[storage] || {}),
                [key]: value,
            },
        });
    };

    return [(store.activePage?.[storage]?.[key] as PolotnoActivePageStateValue<K> | undefined) ?? defaultValue, setter] as const;
};

export const useSetPolotnoActivePageState = <K extends PolotnoStoreActivePageKeys>(
    key: K
): ((value: PolotnoActivePageStateValue<K>) => void) => {
    const [_, setter] = usePolotnoActivePageState(key);

    return setter;
};

export const getPageProperty = <K extends PolotnoStoreActivePageKeys>(page: Page, key: K): PolotnoActivePageStateValue<K> => {
    const { persistence } = polotnoStorePageDefaults[key];
    const storage = persistence ? 'custom' : ('temporary' as unknown as 'custom');

    return page?.[storage]?.[key] ?? polotnoStorePageDefaults[key].defaultValue;
};

export const setPageProperty =
    <K extends PolotnoStoreActivePageKeys>(page: Page, key: K) =>
    (value: PolotnoActivePageStateValue<K>): void => {
        const { persistence } = polotnoStorePageDefaults[key];
        const storage = persistence ? 'custom' : ('temporary' as unknown as 'custom');

        return page.set({
            [storage]: {
                ...(page[storage] || {}),
                [key]: value,
            },
        });
    };
